import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { getDashboardOverview, getDailyMotivation } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { HealthAlerts } from '../components/HealthAlerts';
import { TodayWithNanoukStack } from '../components/TodayWithNanoukStack';
import { WeatherBriefCard } from '../components/WeatherBriefCard';
import { EnergyBalanceCard } from '../components/EnergyBalanceCard';
import { WeeklySummaryCard } from '../components/WeeklySummaryCard';
import confetti from 'canvas-confetti';
import SymptomPredictionBanner from '../components/SymptomPredictionBanner';
import AnniversaryBanner from '../components/AnniversaryBanner';
import { NanoukLevelCard, NanoukAvatar, getNanoukImage } from '../components/NanoukLevel';
import { ReadinessScoreCard } from '../components/ReadinessScore';
import Onboarding from '../components/Onboarding';
import QuickActions from '../components/QuickActions';
import { WeeklyReport } from '../components/WeeklyReport';
import { FirstDayPlanSheet } from '../components/FirstDayPlanSheet';
import { DailyInsights } from '../components/DailyInsights';
import { NanoukJournal } from '../components/NanoukJournal';
import { TaskReminder } from '../components/TaskReminder';
import { NanoukInbox } from '../components/NanoukInbox';
import { GoalsWidget } from '../components/GoalsWidget';
import { MoodTracker } from '../components/MoodTracker';
import { ChallengeProgress } from '../components/ChallengeProgress';
import { MetricSparkline } from '../components/MetricSparkline';
import { PantryQuickPicks } from '../components/PantryQuickPicks';
import { StatsGrid } from '../components/dashboard/StatsGrid';
import { useSparklines } from '../hooks/useSparklines';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
    Apple,
    Moon,
    Dumbbell,
    Plane,
    Droplets,
    Flame,
    Target,
    Trophy,
    Zap,
    TrendingUp,
    Clock,
    CheckCircle2,
    HeartPulse,
    CalendarDays,
    Sunrise,
    Sparkles,
    Loader2
} from 'lucide-react';

const container = {
    hidden: { opacity: 0 },
    show: {
        opacity: 1,
        transition: { staggerChildren: 0.1 }
    }
};

const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
};

export default function Dashboard() {
    const { user, updateProfile, loading: authLoading } = useAuth();
    const { language } = useLanguage();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [motivation, setMotivation] = useState(null);
    const [showOnboarding, setShowOnboarding] = useState(false);
    const [showWeeklyReport, setShowWeeklyReport] = useState(false);
    const [userName, setUserName] = useState('');
    const [loading, setLoading] = useState(true);
    const [morningRoutineLoading, setMorningRoutineLoading] = useState(false);
    const { data: sparkData, range: sparkRange, setRange: setSparkRange, loading: sparkLoading, reload: reloadSparklines } = useSparklines(7);
    const sparkM = sparkData?.metrics || {};

    const rangeToPeriod = (d) => (d === 7 ? 'week' : d === 30 ? 'month' : 'ytd');
    const openMetric = (metric) => {
        if (metric === 'weight') {
            navigate('/health?focus=body');
        } else {
            navigate(`/statistics?period=${rangeToPeriod(sparkRange)}&focus=${metric}`);
        }
    };

    const API = process.env.REACT_APP_BACKEND_URL;

    // Confetti welcome when the user lands on Dashboard via the extreme-weather
    // push notification (URL marker ?from=weather-push). Fires once, then
    // clears the marker so a manual reload doesn't re-trigger it.
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const params = new URLSearchParams(window.location.search);
        if (params.get('from') !== 'weather-push') return;
        // Strip the param so reloads don't keep firing
        params.delete('from');
        const newUrl = window.location.pathname + (params.toString() ? `?${params}` : '');
        window.history.replaceState({}, '', newUrl);
        // Stagger 3 bursts for a richer effect
        const fire = (originX) => confetti({
            particleCount: 70,
            spread: 75,
            origin: { x: originX, y: 0.35 },
            colors: ['#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#a855f7'],
            scalar: 1.1,
        });
        fire(0.2);
        setTimeout(() => fire(0.8), 180);
        setTimeout(() => fire(0.5), 360);
        toast.success(
            language === 'de'
                ? '🐺 Schön dass du reagiert hast! Hier ist deine Strategie für heute:'
                : "🐺 Glad you tapped through! Here's your strategy for today:",
            { duration: 5000 }
        );
    }, [language]);

    const runMorningRoutine = async () => {
        if (morningRoutineLoading) return;
        setMorningRoutineLoading(true);
        try {
            const res = await axios.post(`${API}/api/dashboard/morning-routine`, {});
            const r = res.data || {};
            if (r.items_created > 0) {
                const parts = [];
                if (r.sleep?.created) parts.push(language === 'de' ? 'Schlaf' : 'Sleep');
                const medsCreated = (r.medications || []).filter(m => m.created).length;
                if (medsCreated > 0) parts.push(`${medsCreated} ${language === 'de' ? 'Medi' : 'meds'}`);
                if (r.water?.created) parts.push(language === 'de' ? '250 ml Wasser' : '250ml water');
                toast.success(
                    (language === 'de' ? '☀️ Morgenroutine: ' : '☀️ Morning routine: ') +
                    parts.join(' · ') +
                    (r.xp_earned ? ` (+${r.xp_earned} XP)` : '')
                );
                loadData();
            } else {
                toast.info(language === 'de' ? '☀️ Morgenroutine bereits erledigt für heute' : "☀️ Morning routine already done for today");
            }
        } catch (e) {
            toast.error(language === 'de' ? 'Fehler bei der Morgenroutine' : 'Morning routine failed');
        } finally {
            setMorningRoutineLoading(false);
        }
    };

    // Check for onboarding
    useEffect(() => {
        const needsOnboarding = localStorage.getItem('bestbuddy_needs_onboarding');
        const savedName = localStorage.getItem('bestbuddy_user_name');
        if (needsOnboarding === 'true') {
            setShowOnboarding(true);
            setUserName(savedName || user?.name || '');
        }
    }, [user]);

    // Wait for auth to be ready before loading data
    useEffect(() => {
        if (!showOnboarding && !authLoading && user) {
            loadData();
        }
    }, [showOnboarding, authLoading, user]);

    const loadData = async () => {
        try {
            const [dashboardRes, motivationRes] = await Promise.all([
                getDashboardOverview(),
                getDailyMotivation()
            ]);
            setData(dashboardRes.data);
            setMotivation(motivationRes.data);
        } catch (error) {
            console.error('Failed to load dashboard:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleOnboardingComplete = async (onboardingData) => {
        try {
            await updateProfile({
                first_name: onboardingData.name,
                fitness_goals: onboardingData.goals,
                taler_enabled: !!onboardingData.talerEnabled,
                taler_daily_budget: 18,
            });
            localStorage.removeItem('bestbuddy_needs_onboarding');
            localStorage.removeItem('bestbuddy_user_name');
            toast.success(language === 'de' ? 'Willkommen bei BESTBUDDY! Nanouk freut sich auf dich!' : 'Welcome to BESTBUDDY! Nanouk is excited to meet you!');
            setShowOnboarding(false);
        } catch (error) {
            console.error('Failed to save onboarding data:', error);
            localStorage.removeItem('bestbuddy_needs_onboarding');
            setShowOnboarding(false);
        }
    };

    // Show onboarding if needed
    if (showOnboarding) {
        return <Onboarding onComplete={handleOnboardingComplete} userName={userName} />;
    }

    if (loading) {
        return (
            <div className="p-6 space-y-6">
                <Skeleton className="h-32 w-full rounded-3xl" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => (
                        <Skeleton key={i} className="h-40 rounded-2xl" />
                    ))}
                </div>
            </div>
        );
    }

    const calorieProgress = data?.nutrition ? (data.nutrition.calories / data.nutrition.calorie_goal) * 100 : 0;
    const waterProgress = data?.nutrition ? (data.nutrition.water_ml / data.nutrition.water_goal) * 100 : 0;
    const sleepProgress = data?.sleep ? (data.sleep.avg_hours / data.sleep.goal_hours) * 100 : 0;
    
    // Calculate XP needed for next level (100 XP per level)
    const currentLevel = data?.user?.level || 1;
    const currentXp = data?.user?.total_points || data?.user?.points || 0;
    const xpForCurrentLevel = (currentLevel - 1) * 100;
    const xpProgress = Math.max(currentXp - xpForCurrentLevel, 0);
    const xpNeeded = 100;

    return (
        <div className="p-4 lg:p-8 pb-24 lg:pb-8" data-testid="dashboard-page">
            <FirstDayPlanSheet />
            <motion.div
                variants={container}
                initial="hidden"
                animate="show"
                className="max-w-7xl mx-auto space-y-6"
            >
                {/* ===== Zone 1: Willkommen & Tages-Check-in ===== */}
                <motion.div variants={item}>
                    <Card className="bg-card border overflow-hidden">
                        <CardContent className="p-6 lg:p-8">
                            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                                <div className="flex items-center gap-4">
                                    {/* Nanouk Avatar with Level */}
                                    <NanoukAvatar level={currentLevel} size="lg" />
                                    <div>
                                        <h1 className="font-display text-2xl lg:text-3xl font-bold mb-1">
                                            Hey {user?.first_name || user?.name?.split(' ')[0] || 'Buddy'}! 🐺
                                        </h1>
                                        <p className="text-muted-foreground text-sm lg:text-base max-w-xl">
                                            {motivation?.motivation || 'Wuff! Lass uns heute gemeinsam durchstarten, Buddy!'}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4">
                                    <div className="flex items-center gap-2 bg-background/80 backdrop-blur rounded-full px-4 py-2">
                                        <Zap className="w-5 h-5 text-orange-500" />
                                        <span className="font-bold">{data?.user?.streak || 0}</span>
                                        <span className="text-sm text-muted-foreground">Tage Streak</span>
                                    </div>
                                    <div className="flex items-center gap-2 bg-background/80 backdrop-blur rounded-full px-4 py-2">
                                        <Trophy className="w-5 h-5 text-yellow-500" />
                                        <span className="font-bold">Level {data?.user?.level || 1}</span>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>

                {/* Conditional banners */}
                <motion.div variants={item}>
                    <AnniversaryBanner preview={typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('anniv') === 'preview'} />
                    <SymptomPredictionBanner />
                </motion.div>

                {/* Morning Routine — One-Tap Auto-Log */}
                <motion.div variants={item}>
                    <button
                        onClick={runMorningRoutine}
                        disabled={morningRoutineLoading}
                        className="w-full p-3.5 rounded-xl bg-gradient-to-r from-amber-50 via-orange-50 to-rose-50 dark:from-amber-950/30 dark:via-orange-950/30 dark:to-rose-950/30 border-2 border-amber-200 dark:border-amber-900 hover:border-amber-400 dark:hover:border-amber-700 transition-all flex items-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed"
                        data-testid="morning-routine-btn"
                    >
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shrink-0 shadow-sm">
                            {morningRoutineLoading
                                ? <Loader2 className="w-5 h-5 text-white animate-spin" />
                                : <Sunrise className="w-5 h-5 text-white" />}
                        </div>
                        <div className="flex-1 min-w-0 text-left">
                            <p className="font-semibold text-sm text-foreground flex items-center gap-1.5">
                                {language === 'de' ? 'Morgenroutine starten' : 'Start Morning Routine'}
                                <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                            </p>
                            <p className="text-[11px] text-muted-foreground truncate">
                                {language === 'de'
                                    ? 'Schlaf + Standard-Medis + erstes Glas Wasser in 1 Tap'
                                    : 'Sleep + standard meds + first water glass in 1 tap'}
                            </p>
                        </div>
                    </button>
                </motion.div>

                {/* Mood Tracker - Quick 2-second check */}
                <motion.div variants={item}>
                    <MoodTracker onMoodLogged={() => loadData()} />
                </motion.div>

                {/* ===== Zone 2: Schnellaktionen (unified, near top) ===== */}
                <motion.div variants={item} data-testid="quick-actions-container">
                    <QuickActions 
                        language={language} 
                        onActionComplete={loadData}
                    />
                </motion.div>

                {/* ===== Zone 3+4: Tages-Status (links) & Nanouk-Begleiter (rechts) ===== */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                    {/* Left: core metrics, tasks, goals */}
                    <div className="lg:col-span-7 space-y-6">
                        <StatsGrid data={data} item={item} language={language} loadData={loadData} openMetric={openMetric} sparkData={sparkData} sparkLoading={sparkLoading} sparkRange={sparkRange} setSparkRange={setSparkRange} reloadSparklines={reloadSparklines} waterProgress={waterProgress} sleepProgress={sleepProgress} calorieProgress={calorieProgress} sparkM={sparkM} />

                        <TaskReminder language={language} />

                        <GoalsWidget language={language} />

                        <motion.div variants={item}>
                            <ChallengeProgress />
                        </motion.div>

                        <motion.div variants={item}>
                            <EnergyBalanceCard compact />
                        </motion.div>
                    </div>

                    {/* Right: Nanouk AI companion & insights */}
                    <div className="lg:col-span-5 space-y-6">
                        <motion.div variants={item}>
                            <NanoukLevelCard 
                                level={currentLevel} 
                                xp={xpProgress} 
                                totalXp={xpNeeded} 
                            />
                        </motion.div>

                        <motion.div variants={item}>
                            <ReadinessScoreCard />
                        </motion.div>

                        <motion.div variants={item}>
                            <HealthAlerts onDashboard={true} />
                            <NanoukInbox />
                        </motion.div>

                        {/* Nanouk Journal — collapsible */}
                        <NanoukJournal language={language} />

                        <motion.div variants={item}>
                            <DailyInsights />
                        </motion.div>

                        <motion.div variants={item}>
                            <WeatherBriefCard />
                        </motion.div>

                        <motion.div variants={item}>
                            <TodayWithNanoukStack />
                        </motion.div>
                    </div>
                </div>

                {/* ===== Zone 5: Wochenrückblick & Trends ===== */}
                <motion.div variants={item}>
                    <Dialog open={showWeeklyReport} onOpenChange={setShowWeeklyReport}>
                        <DialogTrigger asChild>
                            <Button 
                                variant="outline" 
                                className="w-full h-14 bg-gradient-to-r from-violet-50 to-indigo-50 dark:from-violet-950/30 dark:to-indigo-950/30 border-violet-200 dark:border-violet-800 hover:border-violet-400 dark:hover:border-violet-600"
                                data-testid="weekly-report-btn"
                            >
                                <CalendarDays className="w-5 h-5 mr-3 text-violet-600 dark:text-violet-400" />
                                <span className="font-medium text-violet-700 dark:text-violet-300">
                                    {language === 'de' ? '📊 Wochenrückblick anzeigen' : '📊 View Weekly Report'}
                                </span>
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                    <CalendarDays className="w-5 h-5 text-violet-500" />
                                    {language === 'de' ? 'Wochenrückblick' : 'Weekly Report'}
                                </DialogTitle>
                            </DialogHeader>
                            <WeeklyReport onClose={() => setShowWeeklyReport(false)} />
                        </DialogContent>
                    </Dialog>
                </motion.div>

                <motion.div variants={item}>
                    <WeeklySummaryCard />
                </motion.div>
            </motion.div>
        </div>
    );
}
